import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { ProjectList } from './controls/ProjectList';
import { ProjectSummary } from './controls/ProjectSummary';
import { DrillholeSummary } from './controls/DrillholeSummary';
import { checkLoggedIn } from '../functions/CookieHelper';
import { membershipSignature } from '../drillholeSummary';


export const CustomerOverview = () => {
    const { t } = useTranslation();
    const [loggedIn] = React.useState(checkLoggedIn());
    const [loading, setLoading] = useState(true);
    const [customerMap, setCustomerMap] = useState({});
    const [selectedProject, setSelectedProject] = useState({});
    const [selectedDrillhole, setSelectedDrillhole] = useState({});
    const [globalSearch, setGlobalSearch] = useState('');
    const [globalSearchResults, setGlobalSearchResults] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [deepLinkMissing, setDeepLinkMissing] = useState(null);
    // Per-drillhole figures for the selected project, keyed by drillhole name.
    // Fetched separately from the map and never required: until they arrive, or
    // if they never do, every hole renders exactly as it did before they existed.
    // Tagged with the project they belong to, so a refetch after a survey moves
    // keeps the old figures on screen until the new ones land (no flicker), while
    // switching project never shows the previous project's figures.
    const [summaryState, setSummaryState] = useState({ project: null, data: {} });

    const handleProjectSelect = (projectName) => {
        let project = customerMap.projects.find((project) => project.name === projectName);
        setSelectedProject(project);
        setSelectedDrillhole({});
    }

    const handleProjectDelete = (projectName) => {
        fetch('api/projects/deleteproject', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'ProjectName': projectName
            }
        })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', JSON.stringify(data, null, 2));
                handleMapUpdated(data);
            });
    }

    const handleDrillholeSelect = (drillholeName) => {
        let drillhole = selectedProject.drillholes.find((drillhole) => drillhole.name === drillholeName);
        setSelectedDrillhole(drillhole);
    }

    const handleGlobalSearchChange = (event) => {
        const searchTerm = event.target.value;
        setGlobalSearch(searchTerm);

        if (!searchTerm.trim()) {
            setGlobalSearchResults([]);
            return;
        }

        const results = [];
        customerMap.projects?.forEach(project => {
            project.drillholes?.forEach(drillhole => {
                if (drillhole.name.toLowerCase().includes(searchTerm.toLowerCase())) {
                    results.push({ projectName: project.name, drillhole });
                }
            });
        });
        setGlobalSearchResults(results);
    };

    const handleSearchResultClick = (projectName, drillholeName) => {
        let project = customerMap.projects.find((p) => p.name === projectName);
        setSelectedProject(project);
        let drillhole = project.drillholes.find((d) => d.name === drillholeName);
        setSelectedDrillhole(drillhole);
        setGlobalSearch('');
        setGlobalSearchResults([]);
    };

    const handleMapUpdated = (newMap) => {
        setCustomerMap(newMap);

        // Preserve selected project and drillhole if they still exist
        if (selectedProject?.name) {
            const updatedProject = newMap.projects?.find(p => p.name === selectedProject.name);
            if (updatedProject) {
                setSelectedProject(updatedProject);

                if (selectedDrillhole?.name) {
                    const updatedDrillhole = updatedProject.drillholes?.find(d => d.name === selectedDrillhole.name);
                    if (updatedDrillhole) {
                        setSelectedDrillhole(updatedDrillhole);
                    } else {
                        setSelectedDrillhole({});
                    }
                }
            } else {
                setSelectedProject({});
                setSelectedDrillhole({});
            }
        }
    }

    useEffect(() => {
        if (!loggedIn) {
            return;
        }
        setLoading(true);
        setCustomerMap({});
        setSelectedProject({});
        const loadCustomer = async () => {
            const response = await fetch('api/projects');
            const data = await response.json();
            setCustomerMap(data);
            setLoading(false);
        }
        loadCustomer();

    }, [loggedIn]);

    // ?project=&drillhole= selects a hole on arrival, which is what the Bulk
    // Review page's "Navigate to Drillhole" link uses. There is no deep link to a
    // single survey: an approved survey is chosen from the Table tab's picker,
    // and pre-selecting one would need plumbing through two more components for
    // no gain. Params are consumed once resolved, leaving a clean URL - the same
    // shape as SurveyApproval's ?survey= link.
    useEffect(() => {
        const projectName = searchParams.get('project');
        const drillholeName = searchParams.get('drillhole');
        if (!projectName || !customerMap.projects) {
            return;
        }
        const project = customerMap.projects.find(p => p.name === projectName);
        const drillhole = project && drillholeName
            ? (project.drillholes || []).find(d => d.name === drillholeName)
            : null;

        if (project) {
            setSelectedProject(project);
            setSelectedDrillhole(drillhole || {});
        }
        if (!project || (drillholeName && !drillhole)) {
            setDeepLinkMissing(drillholeName ? `${projectName} / ${drillholeName}` : projectName);
        }
        setSearchParams({}, { replace: true });
    }, [customerMap.projects, searchParams, setSearchParams]);

    // Re-fetched when the project changes or its surveys move, not on every map
    // update: a metadata edit or completion toggle replaces the project object
    // without changing anything a summary is built from.
    const selectedProjectName = selectedProject?.name;
    const membership = useMemo(() => membershipSignature(selectedProject), [selectedProject]);
    useEffect(() => {
        if (!selectedProjectName) {
            return undefined;
        }
        let cancelled = false;
        fetch('api/projects/summaries?project=' + encodeURIComponent(selectedProjectName))
            .then(response => (response.ok ? response.json() : {}))
            .then(data => { if (!cancelled) setSummaryState({ project: selectedProjectName, data: data || {} }); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [selectedProjectName, membership]);
    const summaries = summaryState.project === selectedProjectName ? summaryState.data : {};

    const handleIsCompletedToggle = () => {
        let complete = !selectedProject.isCompleted;
        setLoading(true);
        fetch('api/projects/markprojectcomplete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'project': selectedProject.name,
                'isCompleted': complete
            },
        })
            .then(response => response.json())
            .then(data => {
                handleMapUpdated(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error marking project as completed:', error);
                setLoading(false);
            });
    }

    function renderProjectCompletion() {
        if (!selectedProject || !selectedProject.name) {
            return "";
        }
        return (
            <div className="d-flex" style={{ marginTop: 10 }}>
            <h5>{t('overview.projectCompleted')}</h5>
                <div className="form-check form-switch" style={{marginLeft:10}}>
                    <input className="form-check-input" type="checkbox" id="isCompletedToggle" checked={selectedProject.isCompleted} onChange={handleIsCompletedToggle} disabled={loading} />
                    <label className="form-check-label" htmlFor="isCompletedToggle">{selectedProject.isCompleted ? t('common.yes') : t('common.no')}</label>
                </div>
            </div >
        );
    }


return loggedIn ? (
    <div className="container-fluid overflow-auto">
        <div className="row" style={{ height: 0 }}></div>
        {deepLinkMissing && (
            <div className="alert alert-warning alert-dismissible" role="alert">
                {/* Two keys around the name rather than <Trans> with a value: the name is the
                    customer's own text, and Trans would parse any tags inside it. */}
                {t('overview.couldNotFind')} <strong>{deepLinkMissing}</strong>{t('overview.renamedOrDeleted')}
                <button type="button" className="btn-close" onClick={() => setDeepLinkMissing(null)}></button>
            </div>
        )}
        {loading ? <p><em>{t('overview.loadingProjects')}</em></p> :
            (
                <div className="row">
                    <div className="col-md-4  col-lg-3">
                        <div className="d-flex justify-content-center">
                            {customerMap.logoUrl && <div className="logoFill" style={{
                                minWidth: 256, height: 128, margin: 5,
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundImage: `url("${customerMap.logoUrl}")`
                            }} ></div>}
                        </div>
                        <div className="mb-3 position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('overview.searchAllDrillholes')}
                                value={globalSearch}
                                onChange={handleGlobalSearchChange}
                            />
                            {globalSearch && globalSearchResults.length > 0 && (
                                <div className="list-group position-absolute" style={{ zIndex: 1000, width: '100%' }}>
                                    {globalSearchResults.slice(0, 10).map((result, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className="list-group-item list-group-item-action"
                                            onClick={() => handleSearchResultClick(result.projectName, result.drillhole.name)}
                                        >
                                            {result.drillhole.name}
                                            <small className="text-muted ms-2">({result.projectName})</small>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <ProjectList projects={customerMap.projects} handleProjectSelect={handleProjectSelect} handleMapUpdated={handleMapUpdated} />
                        {renderProjectCompletion()}
                        <hr />
                        <ProjectSummary project={selectedProject} summaries={summaries} handleDrillholeSelect={handleDrillholeSelect} handleMapUpdated={handleMapUpdated} handleProjectDelete={handleProjectDelete} />
                        <hr style={{ marginTop: 10 }} />
                    </div>
                    <div className="col-md-8 col-lg-9">
                        {/*{customerMap && customerMap.name}*/}
                        <DrillholeSummary project={selectedProject} drillhole={selectedDrillhole} summary={summaries[selectedDrillhole?.name]} handleDrillholeUpdate={(map) => { handleMapUpdated(map); }} industry={customerMap.industry} />
                    </div>
                </div>
            )
        }
    </div>
) : (<div>
    <h1>{t('overview.notLoggedIn')}</h1>
    <p><Trans i18nKey="overview.pleaseLogin" components={{ link: <Link to="/login?returnPage=/overview" /> }} /></p>
</div>)
}
